// mission-secrets-test-env-parser-and-permissions.ts — phase 04a (spec V2 §4.2).
//
// Hai phần thuần của `test.env`, tách khỏi loader để giữ trần dòng:
//  - parser `KEY=VALUE` mỗi dòng: KHÔNG interpolation (`$HOME` giữ nguyên văn),
//    KHÔNG `export` (dòng `export K=V` là dòng sai), bỏ dòng trống và dòng `#`;
//    giá trị = mọi thứ sau dấu `=` đầu tiên, nguyên văn (bỏ `\r` của CRLF).
//    Review 04a L3: bỏ BOM đầu file; key `__proto__`/`constructor`/`prototype`
//    và dòng sai dạng KHÔNG nạp (doctor cảnh báo theo tên key/số dòng); map
//    kết quả không có prototype.
//  - kiểm quyền trên stat ĐÃ LẤY QUA FD (review 04a M1 — không resolve lại
//    đường dẫn): thư mục 0700, file 0600, owner = uid của account chạy `run`
//    (lệch với `actor_binding.os_account` ⇒ doctor cảnh báo, review 04a M4).
// Không hàm nào ở đây trả hay in GIÁ TRỊ — chỉ tên key/số dòng/đường dẫn.
import type { Stats } from "node:fs";
import { KNOWN_SECRET_VALUE_MIN_LENGTH } from "./known-secret-value-encoded-variants.js";

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const PROTOTYPE_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const REQUIRED_DIR_MODE = 0o700;
const REQUIRED_FILE_MODE = 0o600;

export interface ParsedMissionTestEnv {
  values: Record<string, string>;
  /** Số dòng (1-based) không đúng dạng `KEY=VALUE` — chỉ số dòng, không nội dung. */
  malformedLines: number[];
  /** Key hợp dạng nhưng bị từ chối (`__proto__`…) — chỉ tên. */
  rejectedKeys: string[];
}

export function parseMissionTestEnvText(text: string): ParsedMissionTestEnv {
  const values: Record<string, string> = Object.create(null);
  const malformedLines: number[] = [];
  const rejectedKeys: string[] = [];
  const body = text.startsWith("﻿") ? text.slice(1) : text;
  body.split("\n").forEach((rawLine, index) => {
    const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
    const trimmed = line.trim();
    if (trimmed.length === 0 || trimmed.startsWith("#")) return;
    const eq = line.indexOf("=");
    const key = eq > 0 ? line.slice(0, eq) : "";
    if (!KEY_PATTERN.test(key)) {
      malformedLines.push(index + 1);
      return;
    }
    if (PROTOTYPE_KEYS.has(key)) {
      rejectedKeys.push(key);
      return;
    }
    values[key] = line.slice(eq + 1);
  });
  return { values, malformedLines, rejectedKeys };
}

/** Phần trong một cặp nháy bao ngoài khớp nhau (`"v"`/`'v'`), không có ⇒ `undefined`. */
function unquotedTestEnvValue(value: string): string | undefined {
  const quoted = /^(["'])(.*)\1$/s.exec(value);
  return quoted?.[2];
}

/** `"`, `'` hay khoảng trắng — ký tự cú pháp/copy-paste ở rìa giá trị, không phải bí mật. */
function isEdgeNoise(ch: string): boolean {
  return ch === '"' || ch === "'" || /\s/.test(ch);
}

/**
 * Lõi giá trị: cắt MỌI `"`, `'` và khoảng trắng ở HAI rìa (`"'"v'"` ⇒ `v`,
 * `  v  ` ⇒ `v`) — quét tuyến tính, không bóc theo cặp/lớp. Lõi không bao giờ
 * bắt đầu/kết thúc bằng nháy ⇒ không nuốt delimiter JSON (Codex F10); output
 * recipe in bản đã trim vẫn khớp (red-team R2). MỘT định nghĩa cho redaction,
 * cảnh báo giá trị ngắn, toàn chữ số và trùng shell (re-review vòng 3 M1).
 */
export function testEnvValueCore(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && isEdgeNoise(value.charAt(start))) start++;
  while (end > start && isEdgeNoise(value.charAt(end - 1))) end--;
  return value.slice(start, end);
}

/**
 * Tên key có lõi giá trị < 8 ký tự (`testEnvValueCore` — review 04a L9, M1) ⇒
 * KHÔNG được che theo giá trị. Doctor (`test.env`) và reader stdin (review 04b
 * L3) cùng dùng — chỉ trả tên key.
 */
export function keysWithValuesTooShortToMask(values: Record<string, string>): string[] {
  return Object.entries(values)
    .filter(([, v]) => testEnvValueCore(v).length < KNOWN_SECRET_VALUE_MIN_LENGTH)
    .map(([k]) => k);
}

/**
 * Giá trị để che (không phải để cấp recipe — giá trị cấp KHÔNG đổi): lõi
 * (`testEnvValueCore`) + base64 của giá trị gốc + base64 của giá trị bỏ đúng
 * MỘT cặp nháy ngoài (nếu có, và khác lõi). Không đăng ký dạng có nháy/khoảng trắng rìa: mọi
 * biến thể raw/JSON-escape/URL của chúng đều chứa nguyên lõi, còn đăng ký thì
 * nháy rìa nuốt delimiter JSON ⇒ contract_error (Codex F7/F10). Tối đa 3 dạng,
 * tổng ≤ ~3,7× độ dài giá trị — không phụ thuộc số lớp nháy (Codex F11). Mỗi
 * dạng tự qua ngưỡng 8 ký tự ở registry (lõi < 8 ⇒ không che theo giá trị).
 */
export function redactionFormsOfTestEnvValues(values: Record<string, string>): string[] {
  const forms: string[] = [];
  const base64 = (text: string) => Buffer.from(text, "utf8").toString("base64");
  for (const value of Object.values(values)) {
    const core = testEnvValueCore(value);
    forms.push(core);
    // base64 của lõi đã nằm trong biến thể của lõi (registry) ⇒ chỉ thêm khi khác.
    if (value !== core) forms.push(base64(value));
    const unquoted = unquotedTestEnvValue(value);
    if (unquoted !== undefined && unquoted !== core) forms.push(base64(unquoted));
  }
  return forms;
}

function modeText(mode: number): string {
  return `0${(mode & 0o777).toString(8)}`;
}

/** Vấn đề quyền/owner trên stat thư mục (lstat) + stat file (fstat của fd đã mở) — rỗng ⇒ đúng §4.2. */
export function testEnvPermissionProblems(
  dirPath: string,
  dir: Stats,
  filePath: string,
  file: Stats,
  expectedUid: number | undefined,
): string[] {
  const problems: string[] = [];
  if ((dir.mode & 0o777) !== REQUIRED_DIR_MODE)
    problems.push(`thư mục ${dirPath} có quyền ${modeText(dir.mode)} (cần 0700)`);
  if ((file.mode & 0o777) !== REQUIRED_FILE_MODE)
    problems.push(`file ${filePath} có quyền ${modeText(file.mode)} (cần 0600)`);
  if (expectedUid !== undefined) {
    if (dir.uid !== expectedUid)
      problems.push(`owner của ${dirPath} là uid ${dir.uid}, cần uid ${expectedUid}`);
    if (file.uid !== expectedUid)
      problems.push(`owner của ${filePath} là uid ${file.uid}, cần uid ${expectedUid}`);
  }
  return problems;
}
